import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { twMerge as twm } from 'tailwind-merge'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Field } from '../../models/Field'
import { RecordFactory } from '../../models/RecordFactory'
import { Type } from '../../models/Type'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { Checkbox } from '../common/fields/Checkbox'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { ArrangeableCustomOptions } from '../workflows/ArrangeableCustomOptions'
import { SelectedPhoto } from '../workflows/PhotoPicker'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { FieldModal } from './FieldModal'

type Props = {
    form: Type
    onClose: () => void
    onDelete?: () => void
    isLoading?: boolean
    onSave: (form: Type, deletedFields: Field[], photo?: File) => void
    allowTypeModification?: boolean
    enableAllFields?: boolean
}

export const TypePage = ({
    form,
    onClose,
    onDelete,
    isLoading,
    onSave,
    allowTypeModification = true,
    enableAllFields = true,
}: Props) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [scratchForm, setScratchForm] = useState<Type>(form)
    const [selectedPhoto, setSelectedPhoto] = useState<SelectedPhoto | null>(null)
    const { logAndDisplayError } = useErrorHandler()
    const [fieldsToDelete, setFieldsToDelete] = useState<Field[]>([])

    const editingField = useMemo(() => {
        if (searchParams.get('field') === 'new') {
            return new Field()
        }
        return scratchForm.fields.find((f) => f.id === searchParams.get('field'))
    }, [searchParams, scratchForm.fields])

    const onDragEnd = async (source: Field, destinationSeqnum: number) => {
        const movedResult = await RecordFactory.getInstance().moveField(source, destinationSeqnum)
        if (!movedResult.ok) {
            logAndDisplayError(movedResult.error)
            return
        }
    }

    const onAddNewField = async (field: Field) => {
        const result = await RecordFactory.getInstance().sendField(
            field.clone({ id: undefined, record: scratchForm })
        )
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        const newFieldList =
            typeof result.value === 'string'
                ? [...scratchForm.fields, field]
                : [...scratchForm.fields, result.value]

        setScratchForm((f) => f.clone({ fields: newFieldList }))
    }

    const onUpdateField = async (field: Field) => {
        const result = await RecordFactory.getInstance().sendField(field)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
    }

    const isSaveEnabled = useMemo(() => {
        return Boolean(scratchForm.name)
    }, [scratchForm])

    return (
        <WorkflowPage
            title="Form"
            onClose={onClose}
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        onDelete !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {onDelete !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={onDelete}
                            isLoading={isLoading}
                        >
                            Delete form
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={() =>
                            onSave(
                                scratchForm,
                                fieldsToDelete,
                                selectedPhoto?.type === 'file' ? selectedPhoto.file : undefined
                            )
                        }
                        disabled={isLoading || !isSaveEnabled}
                        isLoading={isLoading}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    {enableAllFields && (
                        <WorkflowPage.PhotoPicker
                            onSelectPhoto={(photoFile) => {
                                setSelectedPhoto({ type: 'file', file: photoFile })
                            }}
                            onClearPhoto={() => setSelectedPhoto(null)}
                            selectedPhoto={selectedPhoto}
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Name (required)"
                        icon={<Icon icon="field.large" background="round" />}
                    >
                        <TextField
                            isFullWidth
                            size="medium"
                            placeholder="Enter name"
                            value={scratchForm.name}
                            onClear={() => setScratchForm((f) => f.clone({ name: '' }))}
                            onChange={(e) => {
                                setScratchForm((f) => f.clone({ name: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Always require"
                        icon={<Icon icon="exclamation.large" background="round" />}
                    >
                        <Select
                            size="medium"
                            // using the Type model is intentional
                            options={Type.expirationStrings().map((o) => ({
                                id: o.id ?? '',
                                title: o.label ?? '',
                                subtitle: o.subtitle ?? '',
                            }))}
                            onSelectOption={(option) => {
                                if (option.id === 'always') {
                                    setScratchForm((c) =>
                                        c?.clone({ always_require: true, expires: undefined })
                                    )
                                }
                                else if (option.id === 'once') {
                                    setScratchForm((c) =>
                                        c?.clone({ always_require: false, expires: undefined })
                                    )
                                }
                                else {
                                    setScratchForm((c) =>
                                        c?.clone({
                                            always_require: undefined,
                                            expires: option.id,
                                        })
                                    )
                                }
                            }}
                            selectedOptionID={scratchForm.expiresString}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="For clients to complete"
                        description="Enable if you’d like clients to complete this form (disable if form is for internal use)"
                        icon={<Icon icon="user3.large" background="round" />}
                        hasHoverEffect
                        secondaryView={
                            <Checkbox
                                isChecked={scratchForm.isPublic}
                                onChange={(e) => setScratchForm((f) => f.clone({ is_public: e }))}
                            />
                        }
                    />

                    <WorkflowPage.IconSection
                        title="Attach a contract"
                        description="This contract will be signed when a client purchases this service"
                        icon={<Icon icon="pen.large" background="round" />}
                    >
                        <ContractSearchableSelect
                            selectedContract={scratchForm.contract}
                            onSelectContract={(contract) => {
                                setScratchForm((f) => f.clone({ contract }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Description"
                        icon={<Icon icon="note.large" background="round" />}
                    >
                        <TextArea
                            size="medium"
                            placeholder="Enter description"
                            isAutoResizing
                            onClear={() => setScratchForm((f) => f.clone({ memo: '' }))}
                            value={scratchForm.memo}
                            onChange={(e) => {
                                setScratchForm((f) => f.clone({ memo: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Fields">
                    <WorkflowPage.IconSection
                        title="Fields"
                        icon={<Icon icon="form2.large" background="round" />}
                        hasHoverEffect={scratchForm.fields.length === 0}
                        hasReducedHoverEffect={scratchForm.fields.length > 0}
                        secondaryView={
                            <Button
                                intent="tertiaryLink"
                                size="small"
                                className="p-0"
                                onClick={() => {
                                    setSearchParams((s) => {
                                        s.set('field', 'new')
                                        return s
                                    })
                                }}
                            >
                                Add
                            </Button>
                        }
                    >
                        {scratchForm.fields.length && (
                            <ArrangeableCustomOptions
                                options={scratchForm.fields.map((f) => ({
                                    id: f.id ?? '',
                                    title: f.label ?? '',
                                    subtitle: `${Field.FIELD_TYPES.get(f.type ?? '')} | ${
                                        f.isPublic ? 'Online' : 'Offline'
                                    } | ${f.required ? 'Required' : 'Optional'}`,
                                    icon: 'form2.small',
                                    seqnum: f.seqnum ?? 0,
                                    field: f,
                                    onClick: () => {
                                        setSearchParams((s) => {
                                            s.set('field', f.id ?? '')
                                            return s
                                        })
                                    },
                                    onDelete: () => {
                                        setFieldsToDelete((fields) => [...fields, f])
                                        setScratchForm((form) =>
                                            form.clone({
                                                fields: form.fields.filter(
                                                    (field) => field.id !== f.id
                                                ),
                                            })
                                        )
                                    },
                                }))}
                                onDragEnd={(source, destination, list) => {
                                    onDragEnd(source.field, destination.field.seqnum ?? 0)

                                    setScratchForm((form) =>
                                        form.clone({
                                            fields: list.map((l, s) =>
                                                l.field.clone({ seqnum: s + 1 })
                                            ),
                                        })
                                    )
                                }}
                                shouldAlignDeleteButtonWithContainingCard={true}
                            />
                        )}
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>
            {editingField && (
                <FieldModal
                    isOpen
                    allowTypeModification={allowTypeModification}
                    title={editingField.id ? `Edit question` : `Add question`}
                    onClose={() => {
                        setSearchParams((s) => {
                            s.delete('field')
                            return s
                        })
                    }}
                    field={editingField}
                    onDone={async (field) => {
                        if (editingField.id) {
                            const clonedForm = scratchForm.clone()
                            clonedForm.fields = scratchForm.fields.map((f) =>
                                f.id === field.id ? field : f
                            )
                            clonedForm.selectedFields = scratchForm.selectedFields.map((f) =>
                                f.id === field.id ? field : f
                            )
                            setScratchForm(clonedForm)
                            onUpdateField(field)
                        }
                        else {
                            await onAddNewField(field)
                        }
                        setSearchParams((s) => {
                            s.delete('field')
                            return s
                        })
                    }}
                />
            )}
        </WorkflowPage>
    )
}
